import { ProductCatalogEmbroideryPlacement } from '../../../../__generated__/graphql/catalog/graphql'
import { hashPrefixed, intlPrettyCurrency } from '../../../../lib/utils'
import { fromPlacementId } from '../../combination-rules/placementIds'
import { EmbroideryTypeKey } from '../../combination-rules/types'
import { deriveNameFromEmbroideryPlacement } from '../../utils/deriveNameFromEmbroideryPlacement'
import { FORM_KIND_BY_TYPE_KEY } from '../embroideryTypeKeys'
import {
  EmbroiderySummaryCheckbox,
  EmbroiderySummarySneakPeek,
  EmbroiderySummaryView,
  ExternalEmbroideryFormKind,
} from '../facade'
import { EmbroideryItem } from '../types'

import type { NewLogoInOrder } from '../../useNewLogoInOrder'

/** Text first, then logo, then icon. Items of one type keep their own order. */
const DISPLAY_RANK: Record<EmbroideryTypeKey, number> = { text: 0, logo: 1, icon: 2 }

/**
 * Turns selected items into everything the summary card prints: one sneak peek per item, a total,
 * and a checkbox beside each sneak peek whose item the shopper may leave out of the bag.
 *
 * Plays the role `utils/deriveEmbroiderySummary.ts` plays for the legacy provider, driven by items
 * instead of by-type selections.
 *
 * @param totalInCentsForItems - What a set of items costs, setup fees included, or undefined when a
 *   piece can't be priced. The total covers unchecked items too, so it does not jump when the
 *   shopper toggles a checkbox.
 * @param setItemEnabledForBag - Called by a checkbox for the one item it belongs to.
 */
export const embroiderySummaryFromItems = ({
  items,
  confirmed,
  hasSavedPreferences,
  locale,
  newLogoInOrder,
  productColorHex,
  region,
  requiredTypeKeys,
  setItemEnabledForBag,
  strings,
  totalInCentsForItems,
  withPricing,
}: {
  items: readonly EmbroideryItem[]
  confirmed: boolean
  hasSavedPreferences: boolean
  locale: Parameters<typeof intlPrettyCurrency>[0]['locale']
  /** The new logo another garment in the order carries, which this logo's setup badge reflects. */
  newLogoInOrder: NewLogoInOrder | undefined
  productColorHex: string | undefined
  region: Parameters<typeof intlPrettyCurrency>[0]['region']
  requiredTypeKeys: readonly EmbroideryTypeKey[]
  setItemEnabledForBag: (instanceId: string, enabledForBag: boolean) => void
  strings: {
    customize: string
    editEmbroidery: string
    embroideryItemLabel: string
    toggleAddEmbroidery: string
    embroideryFormKindTitles: Record<ExternalEmbroideryFormKind, string>
    toggleEmbroiderySelectionAtPlacement: string
  }
  totalInCentsForItems: (items: readonly EmbroideryItem[]) => number | undefined
  withPricing: boolean
}): EmbroiderySummaryView => {
  const backgroundColor = hashPrefixed(productColorHex)
  const isPreferencePreview = !confirmed && hasSavedPreferences

  // Names the type and placement, so a screen reader can tell several checkboxes apart.
  const checkboxFor = (
    item: EmbroideryItem,
    placement: ProductCatalogEmbroideryPlacement
  ): EmbroiderySummaryCheckbox | undefined =>
    requiredTypeKeys.includes(item.typeKey)
      ? undefined
      : {
          label: strings.toggleEmbroiderySelectionAtPlacement
            .replace(
              '{{type}}',
              strings.embroideryFormKindTitles[FORM_KIND_BY_TYPE_KEY[item.typeKey]]
            )
            .replace('{{placement}}', deriveNameFromEmbroideryPlacement(placement)),
          checked: item.enabledForBag,
          disabledByConflict: false,
          onToggle: () => setItemEnabledForBag(item.instanceId, !item.enabledForBag),
        }

  const sneakPeekFor = (item: EmbroideryItem): EmbroiderySummarySneakPeek => {
    const placement = fromPlacementId(item.placementId)
    const shared = {
      id: item.instanceId,
      checkbox: checkboxFor(item, placement),
    }

    switch (item.typeKey) {
      case 'text':
        return {
          ...shared,
          kind: 'TEXT',
          props: {
            backgroundColor,
            textColorDisplayName: item.values.threadColor?.displayName,
            textColorCssValue: item.values.threadColor?.hexCode,
            textFirstLine: item.values.firstLine,
            textFontDisplayName: item.values.font?.displayName,
            textFontCssFamily: item.values.font?.designFile,
            textPlacement: placement,
            textSecondLine: item.values.secondLine,
          },
        }
      case 'icon':
        return {
          ...shared,
          kind: 'ICON',
          props: {
            backgroundColor,
            iconImageSource: item.values.icon?.image.src,
            iconInitials: undefined,
            iconInitialsColorCss: undefined,
            iconInitialsColorDisplayName: undefined,
            iconDesignName: item.values.icon?.image.label,
            iconPlacement: placement,
          },
        }
      case 'logo': {
        const logoFile = item.values.logoFile
        return {
          ...shared,
          kind: 'LOGO_DTC',
          props: {
            backgroundColor,
            imgSrc: logoFile?.preview,
            logoName: logoFile?.name,
            logoPlacement: placement,
            logoColor: logoFile?.digitizationSetting,
            logoNote: logoFile?.notes ?? undefined,
            hasLogoSetup:
              Boolean(item.values.requiresSetup) ||
              (logoFile !== undefined && newLogoInOrder?.logoFile.id === logoFile.id),
          },
        }
      }
    }
  }

  const sneakPeeks = [...items]
    .sort((a, b) => DISPLAY_RANK[a.typeKey] - DISPLAY_RANK[b.typeKey])
    .map(sneakPeekFor)

  const totalInCents = withPricing ? totalInCentsForItems(items) : undefined

  return {
    editEmbroideryLabel: strings.editEmbroidery,
    editEmbroideryText: strings.customize,
    price:
      totalInCents === undefined
        ? undefined
        : intlPrettyCurrency({ unit: 'cents', value: totalInCents, region, locale }),
    toggleAddEmbroideryText:
      isPreferencePreview || confirmed ? strings.embroideryItemLabel : strings.toggleAddEmbroidery,
    sneakPeeks,
  }
}
